// src/components/modals/EditSalaryModal.jsx
import React, { useState, useEffect, useCallback } from 'react'
import { X, DollarSign, Calculator, TrendingUp, TrendingDown, Save } from 'lucide-react'
import toast from 'react-hot-toast'
import salaryService from '../../services/salaryService'

const EditSalaryModal = ({ isOpen, onClose, onSalaryUpdated, salary }) => {
  const [formData, setFormData] = useState({
    basicSalary: 0,
    overtimeDurationInHours: 0,
    extraLeaves: 0,
    allowances: {
      bonus: 0,
      house: 0,
      transport: 0,
      health: 0,
      food: 0,
      overtime: 0
    },
    deductions: {
      prePayments: 0,
      extraLeaves: 0,
      epfEmployee: 0,
      tax: 0
    },
    epfEmployer: 0,
    etfEmployer: 0,
    grossSalary: 0,
    netSalary: 0
  })
  const [loading, setLoading] = useState(false)

  // Calculate totals when form data changes
  const calculateTotals = useCallback(() => {
    const totalAllowances = Object.values(formData.allowances).reduce((sum, val) => sum + parseFloat(val || 0), 0)
    const totalDeductions = Object.values(formData.deductions).reduce((sum, val) => sum + parseFloat(val || 0), 0)
    
    const grossSalary = parseFloat(formData.basicSalary || 0) + totalAllowances
    const netSalary = grossSalary - totalDeductions
    
    setFormData(prev => ({
      ...prev,
      grossSalary: grossSalary,
      netSalary: netSalary
    }))
  }, [formData.basicSalary, formData.allowances, formData.deductions])

  // Initialize form data when salary changes
  useEffect(() => {
    if (salary) {
      setFormData({
        basicSalary: salary.basicSalary || 0,
        overtimeDurationInHours: salary.overtimeDurationInHours || 0,
        extraLeaves: salary.extraLeaves || 0,
        allowances: {
          bonus: salary.allowances?.bonus || 0,
          house: salary.allowances?.house || 0,
          transport: salary.allowances?.transport || 0,
          health: salary.allowances?.health || 0,
          food: salary.allowances?.food || 0,
          overtime: salary.allowances?.overtime || 0
        },
        deductions: {
          prePayments: salary.deductions?.prePayments || 0,
          extraLeaves: salary.deductions?.extraLeaves || 0,
          epfEmployee: salary.deductions?.epfEmployee || 0,
          tax: salary.deductions?.tax || 0
        },
        epfEmployer: salary.epfEmployer || 0,
        etfEmployer: salary.etfEmployer || 0,
        grossSalary: salary.grossSalary || 0,
        netSalary: salary.netSalary || 0
      })
    }
  }, [salary])

  useEffect(() => {
    calculateTotals()
  }, [calculateTotals])

  // Don't render if not open
  if (!isOpen || !salary) return null

  const handleInputChange = (field, value, category = null) => {
    if (category) {
      setFormData(prev => ({
        ...prev,
        [category]: {
          ...prev[category],
          [field]: value
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    try {
      setLoading(true)
      
      await salaryService.updateSalary(salary._id, formData)
      
      toast.success('Salary updated successfully')
      onSalaryUpdated()
    } catch (error) {
      console.error('Error updating salary:', error)
      toast.error(error.response?.data?.message || 'Failed to update salary')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long'
      })
    } catch {
      return 'Invalid date'
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Edit Salary</h3>
                <p className="text-sm text-gray-600">
                  {salary.employee?.fullName} - {formatDate(salary.month)}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Basic Info */}
              <div className="space-y-6">
                {/* Basic Salary Section */}
                <div>
                  <h4 className="text-base font-semibold text-gray-900 mb-3">Basic Information</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Basic Salary (Rs)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.basicSalary}
                        onChange={(e) => handleInputChange('basicSalary', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Overtime Hours
                      </label>
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        value={formData.overtimeDurationInHours}
                        onChange={(e) => handleInputChange('overtimeDurationInHours', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Extra Leaves (Days)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formData.extraLeaves}
                        onChange={(e) => handleInputChange('extraLeaves', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Allowances Section */}
                <div>
                  <h4 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                    <TrendingUp className="h-4 w-4 mr-2 text-green-600" />
                    Allowances
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(formData.allowances).map(([key, value]) => (
                      <div key={key}>
                        <label className="block text-xs font-medium text-gray-700 mb-1 capitalize">
                          {key} (Rs)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={value}
                          onChange={(e) => handleInputChange(key, e.target.value, 'allowances')}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-green-500 focus:border-green-500"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 p-3 bg-green-50 rounded-lg">
                    <p className="text-sm font-medium text-green-800">
                      Total Allowances: Rs {formatCurrency(Object.values(formData.allowances).reduce((sum, val) => sum + parseFloat(val || 0), 0))}
                    </p>
                  </div>
                </div>
              </div>

              {/* Right Column - Deductions & Summary */}
              <div className="space-y-6">
                {/* Deductions Section */}
                <div>
                  <h4 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                    <TrendingDown className="h-4 w-4 mr-2 text-red-600" />
                    Deductions
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(formData.deductions).map(([key, value]) => (
                      <div key={key}>
                        <label className="block text-xs font-medium text-gray-700 mb-1 capitalize">
                          {key.replace(/([A-Z])/g, ' $1').trim()} (Rs)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={value}
                          onChange={(e) => handleInputChange(key, e.target.value, 'deductions')}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-red-500 focus:border-red-500"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 p-3 bg-red-50 rounded-lg">
                    <p className="text-sm font-medium text-red-800">
                      Total Deductions: Rs {formatCurrency(Object.values(formData.deductions).reduce((sum, val) => sum + parseFloat(val || 0), 0))}
                    </p>
                  </div>
                </div>

                {/* Company Contributions */}
                <div>
                  <h4 className="text-base font-semibold text-gray-900 mb-3">Company Contributions</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        EPF Employer (Rs)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.epfEmployer}
                        onChange={(e) => handleInputChange('epfEmployer', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ETF Employer (Rs)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.etfEmployer}
                        onChange={(e) => handleInputChange('etfEmployer', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Salary Summary */}
                <div className="border-t pt-4">
                  <h4 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                    <Calculator className="h-4 w-4 mr-2" />
                    Salary Summary
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                      <span className="text-sm font-medium text-blue-700">Gross Salary:</span>
                      <span className="text-lg font-bold text-blue-900">
                        Rs {formatCurrency(formData.grossSalary)}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                      <span className="text-sm font-medium text-green-700">Net Payable Salary:</span>
                      <span className="text-xl font-bold text-green-900">
                        Rs {formatCurrency(formData.netSalary)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-md transition-colors flex items-center space-x-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    <span>Update Salary</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default EditSalaryModal